import React, { useState, useEffect } from 'react';
import './HeroSectionPolicy.css';

const HeroSectionPolicy = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [isLoading, setIsLoading] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      setContentVisible(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const policies = {
    introduction: {
      title: "Introduction",
      content: "Welcome to HR Copilot's policy framework. Our policies are designed to create a supportive, fair, and productive work environment while ensuring compliance with all applicable laws and regulations.",
      icon: "📋"
    },
    codeOfConduct: {
      title: "Code of Conduct",
      content: "All employees are expected to adhere to our Code of Conduct which promotes professionalism, respect, and integrity in all workplace interactions and activities.",
      icon: "🤝"
    },
    leavePolicy: {
      title: "Leave Policy",
      content: "Our leave policy includes provisions for vacation, sick leave, parental leave, and other types of time off, ensuring employees can maintain work-life balance.",
      icon: "🏖️"
    },
    remoteWork: {
      title: "Remote Work Policy",
      content: "This policy outlines expectations, eligibility, and best practices for remote work arrangements to ensure productivity and engagement regardless of location.",
      icon: "🏠"
    },
    diversity: {
      title: "Diversity & Inclusion",
      content: "We are committed to fostering a diverse and inclusive workplace where all employees feel valued, respected, and empowered to do their best work.",
      icon: "🌍"
    },
    benefits: {
      title: "Employee Benefits",
      content: "Comprehensive benefits package including health insurance, retirement plans, wellness programs, and professional development opportunities.",
      icon: "🎁"
    },
    performance: {
      title: "Performance Management",
      content: "Structured approach to setting goals, providing feedback, and evaluating performance to support employee growth and organizational success.",
      icon: "📊"
    }
  };

  if (isLoading) {
    return (
      <div className="policy-loading-container">
        <div className="policy-loading-animation">
          <div className="policy-pulse-ring"></div>
          <div className="policy-pulse-ring policy-delay-1"></div>
          <div className="policy-pulse-ring policy-delay-2"></div>
          <svg className="policy-logo-icon" width="50" height="50" viewBox="0 0 50 50">
            <path d="M25 5C14.5 5 6 13.5 6 24S14.5 43 25 43S44 34.5 44 24S35.5 5 25 5z M25 10C28.5 10 31.5 12 33 15L17 15C18.5 12 21.5 10 25 10z M15 27C13.5 27 12 25.5 12 24S13.5 21 15 21S18 22.5 18 24S16.5 27 15 27z M25 38C21.5 38 18.5 36 17 33L33 33C31.5 36 28.5 38 25 38z M35 27C33.5 27 32 25.5 32 24S33.5 21 35 21S38 22.5 38 24S36.5 27 35 27z" fill="#6366f1"/>
          </svg>
        </div>
        <p className="policy-loading-text">Loading Policies...</p>
      </div>
    );
  }

  return (
    <div className="policy-page-container">
      {/* Animated SVG Background */}
      <div className="policy-animated-background">
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="policy-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.15">
                <animate attributeName="offset" values="0;1;0" dur="15s" repeatCount="indefinite" />
              </stop>
              <stop offset="50%" stopColor="#10b981" stopOpacity="0.1">
                <animate attributeName="offset" values="0.5;1;0.5" dur="12s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.1">
                <animate attributeName="offset" values="1;0;1" dur="18s" repeatCount="indefinite" />
              </stop>
            </linearGradient>
            
            <pattern id="policy-pattern" x="0" y="0" width="0.05" height="0.05" patternUnits="objectBoundingBox">
              <circle cx="5" cy="5" r="2" fill="#6366f1" opacity="0.4">
                <animate attributeName="r" values="1;3;1" dur="8s" repeatCount="indefinite" />
                <animate attributeName="cx" values="0;10;0" dur="15s" repeatCount="indefinite" />
                <animate attributeName="cy" values="0;10;0" dur="12s" repeatCount="indefinite" />
              </circle>
              <rect x="20" y="20" width="8" height="8" fill="#10b981" opacity="0.3">
                <animate attributeName="width" values="5;10;5" dur="7s" repeatCount="indefinite" />
                <animate attributeName="height" values="5;10;5" dur="7s" repeatCount="indefinite" />
                <animate attributeName="x" values="15;25;15" dur="10s" repeatCount="indefinite" />
                <animate attributeName="y" values="15;25;15" dur="10s" repeatCount="indefinite" />
              </rect>
              <polygon points="40,40 45,35 50,40 45,45" fill="#f59e0b" opacity="0.3">
                <animate attributeName="points" values="40,40 45,35 50,40 45,45; 38,38 45,32 52,38 45,45; 40,40 45,35 50,40 45,45" dur="9s" repeatCount="indefinite" />
              </polygon>
            </pattern>
          </defs>
          <rect x="0" y="0" width="100" height="100" fill="url(#policy-gradient)" />
          <rect x="0" y="0" width="100" height="100" fill="url(#policy-pattern)" opacity="0.2" />
        </svg>
      </div>

      <div className={`policy-content-container ${contentVisible ? 'policy-content-visible' : ''}`}>
        <header className="policy-header-section">
          <h1 className="policy-main-title">
            <span className="policy-title-word policy-title-word-1">HR</span>
            <span className="policy-title-word policy-title-word-2">Copilot</span>
            <span className="policy-title-word policy-title-word-3">Policies</span>
          </h1>
          <p className="policy-header-subtitle">Guidelines for a productive and harmonious workplace</p>
        </header>

        <nav className="policy-navigation">
          {Object.keys(policies).map((key) => (
            <button
              key={key}
              className={`policy-nav-item ${activeSection === key ? 'policy-nav-active' : ''}`}
              onClick={() => setActiveSection(key)}
            >
              <span className="policy-nav-icon">{policies[key].icon}</span>
              <span className="policy-nav-text">{policies[key].title}</span>
            </button>
          ))}
        </nav>

        <main className="policy-main-content">
          <div className="policy-content-card">
            <div className="policy-card-header">
              <h2 className="policy-content-title">{policies[activeSection].title}</h2>
              <div className="policy-content-icon">{policies[activeSection].icon}</div>
            </div>
            <p className="policy-content-text">{policies[activeSection].content}</p>
            
            <div className="policy-expandable-details">
              <div className="policy-detail-section">
                <h3 className="policy-detail-title">Detailed Guidelines</h3>
                <p className="policy-detail-text">Each policy includes detailed guidelines, procedures, and examples to help employees understand expectations and compliance requirements. Managers receive additional training to ensure consistent application of all policies across the organization.</p>
              </div>
              
              <div className="policy-detail-section">
                <h3 className="policy-detail-title">Implementation</h3>
                <p className="policy-detail-text">These policies are implemented through our HR Copilot platform, which provides tools, resources, and support for both employees and managers. Regular reviews ensure our policies remain current with evolving workplace norms and legal requirements.</p>
              </div>
              
              <div className="policy-image-placeholder">
                <div className="policy-image-content">
                  <svg width="100" height="100" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="40" fill="#6366f1" opacity="0.2" />
                    <path d="M30,40 L70,40 L70,70 L30,70 Z" fill="#10b981" opacity="0.3" />
                    <circle cx="50" cy="35" r="10" fill="#f59e0b" opacity="0.4" />
                    <text x="50" y="55" textAnchor="middle" fill="#6366f1" fontSize="14">Policy Visual</text>
                  </svg>
                </div>
                <p className="policy-image-caption">Policy implementation framework</p>
              </div>
            </div>
          </div>
        </main>

        <footer className="policy-footer-section">
          <p className="policy-footer-text">© 2023 HR Copilot. All policies are reviewed annually and subject to updates.</p>
          <div className="policy-footer-links">
            <a href="#" className="policy-footer-link">Policy Updates</a>
            <a href="#" className="policy-footer-link">Contact HR</a>
            <a href="#" className="policy-footer-link">Feedback</a>
            <a href="#" className="policy-footer-link">Resources</a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HeroSectionPolicy;